import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DeleteUserAction } from '@celerofinancas/state-management';
import {
  OAuthService,
  TokenEvents,
  TokenHandlerService,
} from '@celerofinancas/ui-oauth';
import { Store } from '@ngxs/store';
import { Observable, Subject, throwError } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

/**
 * Token refresh interceptor
 */
@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  /**
   * OAuth service
   */
  private readonly oauth = inject(OAuthService);

  /**
   * Token handler service
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * Store management
   */
  private readonly store = inject(Store);

  /**
   * Is refreshing
   */
  private isRefreshing = false;

  /**
   * Emits an event when token is expired
   */
  private tokenExpires$ = new Subject();

  /**
   * White list of URLs
   */
  private readonly whiteList = [
    'static.caixa.celero.mobi',
  ];

  /**
   * Token refresh intercept method
   *
   * @param request Current request
   * @param next Http handler
   */
  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<any> {
    if (this.whiteList.some((url) => request.url.includes(url))) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (
          error.status === 401 &&
          this.tokens.refreshToken &&
          !this.isRefreshing
        ) {
          this.isRefreshing = true;
          return this.oauth.refreshToken().pipe(
            switchMap((data) => {
              return next.handle(
                request.clone({
                  headers: request.headers.set(
                    'Authorization',
                    `Bearer ${data?.access_token ?? this.tokens.accessToken}`
                  ),
                })
              );
            }),
            finalize(() => {
              this.isRefreshing = false;
            })
          );
        } else if (
          error.status === 400 &&
          request.url.includes('refresh_token') &&
          !this.tokens.isRefreshTokenValid
        ) {
          this.tokens.clearTokensFromStorage();
          this.store.dispatch(new DeleteUserAction());
          this.tokenExpires$.next(true);
        }

        if (
          (error.status !== 401 &&
            this.tokens.isAccessTokenValid &&
            this.tokens.isRefreshTokenValid) ||
          (!this.tokens.isAccessTokenValid && !this.tokens.isRefreshTokenValid)
        ) {
          return throwError(() => error);
        }

        return this.tokens?.eventsSubject$.pipe(
          take(1),
          filter((e) => e.event === TokenEvents.TOKEN_RECEIVED),
          switchMap(() => {
            return next.handle(
              request.clone({
                headers: request.headers.set(
                  'Authorization',
                  `Bearer ${this.tokens.accessToken}`
                ),
              })
            );
          }),
          takeUntil(this.tokenExpires$)
        );
      })
    );
  }
}
